/**
 * @file apps/web/src/components/Badge.tsx
 * @description Status badge component — pill-shaped labels for all Veridox status types.
 * All colors use CSS variables from the design system. No hardcoded hex values.
 */

import type { ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BadgeVariant =
  // Test results
  | 'pass' | 'fail' | 'blocked' | 'n_a' | 'not_tested'
  // Defect severity
  | 'critical' | 'high' | 'medium' | 'low'
  // Defect / approval status
  | 'open' | 'in_progress' | 'resolved' | 'closed' | 'wont_fix' | 'deferred'
  // Approval workflow
  | 'pending' | 'approved' | 'rejected' | 'delegated' | 'skipped' | 'cancelled'
  // Document / script status
  | 'draft' | 'ready' | 'passed' | 'failed' | 'archived'
  // Session status
  | 'scheduled' | 'completed'
  // Generic
  | 'info' | 'warning' | 'success' | 'muted'
  // Project phase
  | 'discovery' | 'design' | 'build' | 'uat' | 'go_live' | 'hypercare'
  // Discovery disposition
  | 'not_reviewed' | 'out_of_box' | 'in_erp_custom' | 'ootb_custom' | 'not_applicable'
  // Design item type
  | 'suiteflow' | 'suitescript' | 'custom_field' | 'suiteapp' | 'bundle' | 'configuration' | 'other'
  // Design item status (draft/approved already exist above)
  | 'in_review'
  // Build status (in_progress/not_applicable already exist above)
  | 'not_started' | 'built';

export type BadgeSize = 'xs' | 'sm' | 'md';

interface BadgeProps {
  variant:    BadgeVariant;
  label?:     string;       // if omitted, variant name is formatted as label
  size?:      BadgeSize;
  dot?:       boolean;      // show a leading dot
  className?: string;
  children?:  ReactNode;
}

// ---------------------------------------------------------------------------
// Variant → style mapping (Tailwind classes)
// ---------------------------------------------------------------------------

type StyleMap = {
  bg:     string;
  text:   string;
  dot?:   string;
  // Optional border utility (color + style, e.g. 'border border-dashed
  // border-muted/50') — most variants don't set this (no border at all,
  // preserving existing rendering exactly). Added specifically so
  // not_reviewed can read as an empty/unresolved outline rather than a
  // filled chip — see the not_reviewed entry below.
  border?: string;
};

const STYLES: Record<BadgeVariant, StyleMap> = {
  // Test results
  pass:         { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  fail:         { bg: 'bg-danger/12',   text: 'text-danger',  dot: 'bg-danger' },
  blocked:      { bg: 'bg-orange-100',  text: 'text-orange-700', dot: 'bg-orange-500' },
  n_a:          { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  not_tested:   { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  // Defect severity
  critical:     { bg: 'bg-red-50',      text: 'text-red-700', dot: 'bg-red-500' },
  high:         { bg: 'bg-danger/12',   text: 'text-danger',  dot: 'bg-danger' },
  medium:       { bg: 'bg-warning/12',  text: 'text-warning', dot: 'bg-warning' },
  low:          { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  // Defect status
  open:         { bg: 'bg-danger/10',   text: 'text-danger',  dot: 'bg-danger' },
  in_progress:  { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  resolved:     { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  closed:       { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  wont_fix:     { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  deferred:     { bg: 'bg-warning/12',  text: 'text-warning', dot: 'bg-warning' },
  // Approval
  pending:      { bg: 'bg-warning/12',  text: 'text-warning', dot: 'bg-warning' },
  approved:     { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  rejected:     { bg: 'bg-danger/12',   text: 'text-danger',  dot: 'bg-danger' },
  delegated:    { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  skipped:      { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  cancelled:    { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  // Document / script
  draft:        { bg: 'bg-warning/12',  text: 'text-warning', dot: 'bg-warning' },
  ready:        { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  passed:       { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  failed:       { bg: 'bg-danger/12',   text: 'text-danger',  dot: 'bg-danger' },
  archived:     { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  // Session
  scheduled:    { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  completed:    { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  // Generic
  info:         { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  warning:      { bg: 'bg-warning/12',  text: 'text-warning', dot: 'bg-warning' },
  success:      { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  muted:        { bg: 'bg-gray',        text: 'text-muted',   dot: 'bg-muted' },
  // Phase
  discovery:    { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  design:       { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  build:        { bg: 'bg-warning/12',  text: 'text-warning', dot: 'bg-warning' },
  uat:          { bg: 'bg-sky',         text: 'text-blue',    dot: 'bg-blue' },
  go_live:      { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  hypercare:    { bg: 'bg-success/12',  text: 'text-success', dot: 'bg-success' },
  // Discovery disposition
  // not_reviewed vs. not_applicable used to share a byte-for-byte identical
  // style (bg-gray/text-muted/bg-muted) despite being opposites — one is
  // "no decision yet," the other is "decided, excluded." not_applicable
  // keeps the plain filled-grey treatment already used app-wide for
  // decided/closed states (closed, wont_fix, skipped, cancelled, archived,
  // not_started all use the same pattern) — definite, solid. not_reviewed
  // instead gets no fill and a dashed border, reading as an empty/pending
  // outline rather than a settled state. No new colors — 'muted' is
  // already a registered Tailwind color token (tailwind.config.ts);
  // 'transparent' and 'border-dashed' are core Tailwind utilities, not
  // new tokens.
  not_reviewed:   { bg: 'bg-transparent', text: 'text-muted',   dot: 'bg-muted', border: 'border border-dashed border-muted/50' },
  out_of_box:     { bg: 'bg-success/12', text: 'text-success', dot: 'bg-success' },
  in_erp_custom:  { bg: 'bg-warning/12', text: 'text-warning', dot: 'bg-warning' },
  ootb_custom:    { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
  not_applicable: { bg: 'bg-gray',       text: 'text-muted',   dot: 'bg-muted' },
  // Design item type
  suiteflow:      { bg: 'bg-sky',        text: 'text-blue',    dot: 'bg-blue' },
  suitescript:    { bg: 'bg-warning/12', text: 'text-warning', dot: 'bg-warning' },
  custom_field:   { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
  suiteapp:       { bg: 'bg-success/12', text: 'text-success', dot: 'bg-success' },
  bundle:         { bg: 'bg-sky',        text: 'text-blue',    dot: 'bg-blue' },
  configuration:  { bg: 'bg-warning/12', text: 'text-warning', dot: 'bg-warning' },
  other:          { bg: 'bg-gray',       text: 'text-muted',   dot: 'bg-muted' },
  // Design item status
  in_review:      { bg: 'bg-sky',        text: 'text-blue',    dot: 'bg-blue' },
  // Build status
  not_started:    { bg: 'bg-gray',       text: 'text-muted',   dot: 'bg-muted' },
  built:          { bg: 'bg-success/12', text: 'text-success', dot: 'bg-success' },
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  xs: 'text-[10px] px-1.5 py-0.5 gap-1',
  sm: 'text-[11px] px-2 py-0.5 gap-1.5',
  md: 'text-xs px-2.5 py-1 gap-2',
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** Explicit label overrides for variants whose auto-formatted name is wrong or ambiguous. */
const LABEL_OVERRIDES: Partial<Record<BadgeVariant, string>> = {
  ready:         'Ready to Test',
  n_a:           'N/A',
  wont_fix:      "Won't Fix",
  not_tested:    'Not Tested',
  not_reviewed:  'Not Reviewed',
  out_of_box:    'Out of Box',
  // Display-only rename (docs/NEXT-PHASE-SCOPING.md §1) — enum values
  // (in_erp_custom / ootb_custom) and their meaning are unchanged.
  // in_erp_custom is the SuiteScript/custom-development tier;
  // ootb_custom is the no-code tier (workflows, custom fields, saved
  // searches). Confirmed against uploadPipeline.ts's
  // suggestDispositionForRule() before this rename shipped.
  in_erp_custom: 'Customization',
  ootb_custom:   'ERP Supported Customization',
  not_applicable: 'Not Applicable',
  suiteflow:      'SuiteFlow',
  suitescript:    'SuiteScript',
  suiteapp:       'SuiteApp',
};

function formatLabel(variant: BadgeVariant): string {
  return LABEL_OVERRIDES[variant] ?? variant
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Badge({ variant, label, size = 'sm', dot = false, className = '', children }: BadgeProps) {
  const style = STYLES[variant] ?? STYLES.muted;
  const sizeClass = SIZE_CLASSES[size];
  const text = children ?? label ?? formatLabel(variant);

  return (
    <span
      className={[
        'inline-flex items-center rounded-full font-mono font-medium leading-none whitespace-nowrap',
        style.bg,
        style.text,
        style.border ?? '',
        sizeClass,
        className,
      ].join(' ')}
    >
      {dot && (
        <span className={['rounded-full shrink-0', style.dot ?? '', size === 'xs' ? 'w-1 h-1' : 'w-1.5 h-1.5'].join(' ')} />
      )}
      {text}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Convenience helpers
// ---------------------------------------------------------------------------

export function ResultBadge({ result }: { result: string }) {
  const map: Record<string, BadgeVariant> = {
    pass: 'pass', fail: 'fail', blocked: 'blocked', n_a: 'n_a', not_tested: 'not_tested',
  };
  return <Badge variant={map[result] ?? 'muted'} dot />;
}

export function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, BadgeVariant> = {
    critical: 'critical', high: 'high', medium: 'medium', low: 'low',
  };
  return <Badge variant={map[severity] ?? 'medium'} dot />;
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge variant={(status as BadgeVariant) ?? 'muted'} dot />;
}
